import { ScrollColumnView } from '@ipfabric/ui-components/ScrollView/ScrollColumnView';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type { PathLookupGraphPreset } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import type {
  DeviceViewPacketDetailsEntity,
  PacketDetail as IPacketDetail,
} from 'components/DeviceView/deviceViewTypes';
import { type FC, memo, useEffect, useRef } from 'react';
import type Scrollbars from 'react-custom-scrollbars-2';
import { usePrevious } from 'utils/hooks/usePrevious';
import { useDeviceViewContextSelectors } from '../model/DeviceViewModel';
import { closePathLookupEdgeDetail } from '../model/commands/closePathLookupEdgeDetail';
import { hidePathLookupDetailAnimation } from '../model/commands/hidePathLookupDetailAnimation';
import { PacketDetail } from './PacketDetail/PacketDetail';

const DETAIL_WIDTH = 350;

interface Props {
  deviceViewEntity: DeviceViewPacketDetailsEntity;
  pathLookupPreset: PathLookupGraphPreset;
  visible: boolean;
}

const BareDeviceViewPacketDetails: FC<Props> = (props) => {
  const ctxProps = useDeviceViewContextSelectors({
    dispatch: prop('dispatch'),
    handlersContainer: prop('handlersContainer'),
  });
  const scrollbarsRef = useRef<Scrollbars>(null);
  const { details } = props.deviceViewEntity;
  const prevDetails = usePrevious(details);

  const handleHidePacketDetailAnimation = useStableCallback(({ detailId }) => {
    ctxProps.dispatch(hidePathLookupDetailAnimation, { detailId });
  });

  const handlePathLookupDetailClose = useStableCallback(({ detailId }) => {
    ctxProps.dispatch(closePathLookupEdgeDetail, { detailId });
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: to be fired on details change
  useEffect(() => {
    // Scroll to new detail
    if (
      scrollbarsRef.current &&
      prevDetails &&
      details.length > prevDetails.length
    ) {
      scrollbarsRef.current.scrollLeft(0);
      // Scroll to existing detail when edge is clicked
    } else if (
      scrollbarsRef.current &&
      prevDetails &&
      details.length === prevDetails.length
    ) {
      // We detect that tab is newly open or edge clicked again by finding the one with active animation
      const updatedDetailIndex = details.findIndex(
        (detail: IPacketDetail, index: number) =>
          // Fixed noUncheckedIndexedAccess NIM-19408
          detail.showAnimation && !prevDetails[index]!.showAnimation,
      );
      if (updatedDetailIndex > -1) {
        const shift = updatedDetailIndex !== 0 ? -1 : 0;
        scrollbarsRef.current.scrollLeft(
          (updatedDetailIndex + shift) * DETAIL_WIDTH,
        );
      }
    }
  }, [details]);

  return (
    <ScrollColumnView
      className={classNames(!props.visible && 'hidden')}
      ref={scrollbarsRef}
    >
      <div className='flex flex-1'>
        {details.map((detail: IPacketDetail) => (
          <PacketDetail
            detailId={detail.id}
            deviceViewElement={props.deviceViewEntity.deviceViewElement}
            disableHover={false}
            edgeDirection={detail.edgeDirection}
            key={detail.id}
            labels={detail.labels}
            onHidePacketDetailAnimation={handleHidePacketDetailAnimation}
            onPacketDetailClose={handlePathLookupDetailClose}
            onPacketDetailDestroy={
              ctxProps.handlersContainer.handlePacketDetailLeave
            }
            onPacketDetailHover={
              ctxProps.handlersContainer.handlePacketDetailHover
            }
            onPacketDetailLeave={
              ctxProps.handlersContainer.handlePacketDetailLeave
            }
            packet={detail.packet}
            pathLookupPreset={props.pathLookupPreset}
            showAnimation={detail.showAnimation}
            showEmptyPlaceholder={false}
            sourceLabel={detail.sourceLabel}
            targetLabel={detail.targetLabel}
          />
        ))}
      </div>
    </ScrollColumnView>
  );
};

export const DeviceViewPacketDetails = memo(BareDeviceViewPacketDetails);
